/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import { setToastType } from '../../../../redux/actions';
import { useGetSassTokenQuery } from '../../../../redux/apis/Sass.api';
import {
  usePostRemoveUserAsDelegateMutation,
  useGetEmployeeConfigQuery
} from '../../../../redux/apis/Users.api';

import useToast from '../../../../services/Toast';

import { handleBlobPicUrl, userProfilePicUrl } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps, Employee } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  TextInTable,
  RemoveButton,
  EmployeeAvatar,
  DelegatesConfigContainer,
  DelegatedToMeContainer,
  EmployeeNameInTable,
  Meta,
  DelegateSlot
} from './DelegatedToMeTable.styles';

/**
 * Static File Imports
 */
import defaultImage from '../../../../assets/images/defaultImage.png';

interface DelegatedToMeProps {
  authorizedUser?: AuthUserProps;
}

const DelegatedToMeListTable: FC<DelegatedToMeProps> = ({ authorizedUser }) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState<string>('lastName');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Delegate');

  const { toast } = useToast();

  const {
    data: { delegatedToMeEmployees: delegatedToMeList = [] } = {},
    // isError: isGetEmployeeConfigError,
    // isFetching: isGetEmployeeConfigFetching,
    isLoading: isGetEmployeeConfigLoading
  } = useGetEmployeeConfigQuery(authorizedUser?.hcaid ?? '', {
    pollingInterval: 300000,
    refetchOnMountOrArgChange: true
  });
  const [
    removeUserAsDelegate
    // {
    //   isError: isRemoveUserAsDelegateError,
    //   isLoading: isRemoveUserAsDelegateLoading,
    //   isSuccess: isRemoveUserAsDelegateSuccess
    // }
  ] = usePostRemoveUserAsDelegateMutation();
  const {
    data: sasTokenData,
    // isError: isGetSassTokenError,
    isFetching: isGetSassTokenFetching
    // isLoading: isGetSassTokenLoading
  } = useGetSassTokenQuery();

  const handleRemoveEmployee = (delegate: Employee) => {
    dispatch(setToastType('Delete Delegated To Me'));
    toast(
      'This action cannot be undone.',
      'info',
      null,
      400,
      true,
      `The owner will have to add you as a delegate.`,
      async () => {
        const payload = {
          userId: delegate.hcaid,
          delegateId: authorizedUser?.hcaid ?? ''
        };
        await removeUserAsDelegate(payload);
      }
    );
  };

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Delegate':
        key = 'lastName';
        break;
      case 'Department':
        key = 'department';
        break;
      case 'Title':
        key = 'title';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const filteredList = useMemo(() => {
    const stateCopy = [...delegatedToMeList];
    stateCopy.sort((a: Employee, b: Employee) =>
      a[sortKey as keyof Employee]?.toLowerCase() >
      b[sortKey as keyof Employee]?.toLowerCase()
        ? sortDir === 'asc'
          ? 1
          : -1
        : sortDir === 'asc'
        ? -1
        : 1
    );

    return stateCopy;
  }, [delegatedToMeList, sortKey, sortDir]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Delegate"
      id="Delegated-To-Me-List-Table-Delegate-Column"
      unit="column"
      colWidth={34}
      direction={!(active === 'Delegate') ? 'none' : sortDir}
      sort
    >
      Delegate
    </CellContainer>,
    <CellContainer
      key="Department"
      id="Delegated-To-Me-List-Table-Department-Column"
      unit="column"
      colWidth={27}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Title"
      id="Delegated-To-Me-List-Table-Title-Column"
      unit="column"
      colWidth={27}
      direction={!(active === 'Title') ? 'none' : sortDir}
      sort
    >
      Title
    </CellContainer>,
    <CellContainer
      key="Delete"
      id="Delegated-To-Me-List-Table-Delete-Column"
      unit="column"
      colWidth={12}
      direction="none"
    />
  ];

  const rows = filteredList?.map((delegate: Employee, i: number) => (
    <StyledNeuTableRow
      // eslint-disable-next-line react/no-array-index-key
      key={`Delegated-To-Me-List-Row-${i}`}
      id={`Delegated-To-Me-List-Row-${delegate.hcaid}`}
      unit="row"
      size="default"
      height="5em"
    >
      <CellContainer unit="column" colWidth={34}>
        <DelegateSlot>
          {sasTokenData ? (
            <EmployeeAvatar
              src={
                handleBlobPicUrl(delegate.pictureUrl, sasTokenData.sasToken) ??
                userProfilePicUrl(delegate.hcaid, sasTokenData.sasToken)
              }
              alt="Delegate Image"
              onError={e => {
                e.currentTarget.src = defaultImage;
                return null;
              }}
            />
          ) : (
            <EmployeeAvatar src={defaultImage} alt="Default Image" />
          )}
          <div>
            <EmployeeNameInTable className="my-0">
              {delegate?.employeeName}
            </EmployeeNameInTable>
            <TextInTable className="my-0">{delegate?.hcaid}</TextInTable>
          </div>
        </DelegateSlot>
      </CellContainer>
      <CellContainer unit="column" colWidth={27}>
        <Meta>
          <TextInTable className="my-0">
            {delegate?.department || 'Unknown'}
          </TextInTable>
        </Meta>
      </CellContainer>
      <CellContainer unit="column" colWidth={27}>
        <Meta>
          <TextInTable className="my-0">
            {delegate?.title || 'Unknown'}
          </TextInTable>
        </Meta>
      </CellContainer>
      <CellContainer unit="column" colWidth={12}>
        <RemoveButton
          kind="secondary"
          color="orange"
          size="small"
          onClick={() => handleRemoveEmployee(delegate)}
        >
          Remove
        </RemoveButton>
      </CellContainer>
    </StyledNeuTableRow>
  ));

  return (
    <DelegatedToMeContainer
      id="Delegated-To-Me-Container"
      className="d-flex flex-column w-100"
    >
      <DelegatesConfigContainer />
      <Table
        customClass="delegated-to-me-table"
        headers={headers}
        rows={rows}
        loading={isGetEmployeeConfigLoading || isGetSassTokenFetching}
        type="delegated-to-me"
        onSort={(e: any) => handleSort(e.detail.label)}
      />
    </DelegatedToMeContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser
  };
};
export default connect(mapReduxStateToProps)(DelegatedToMeListTable);
